import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';

// Estilos de terceiros
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';

// Componente Principal e Sobrescritas de Estilo
import App from './App';
import './styles/datepicker-overrides.css';
import { isBenignIosNotReadableError } from './utils/sentryFilters';

function isValidSentryDsn(value) {
  if (!value) return false;

  try {
    const dsnUrl = new URL(value);
    const projectId = dsnUrl.pathname.replace(/\//g, '');

    return (
      dsnUrl.protocol === 'https:' &&
      Boolean(dsnUrl.username) &&
      /^\d+$/.test(projectId) &&
      dsnUrl.hostname.includes('sentry.io')
    );
  } catch {
    return false;
  }
}

const sentryDsn = process.env.REACT_APP_SENTRY_DSN?.trim();
const sentryEnabled = process.env.REACT_APP_SENTRY_ENABLED === 'true';
const sentryAllowSend = process.env.REACT_APP_SENTRY_ALLOW_SEND === 'true';

if (
  process.env.REACT_APP_ENV === 'production' &&
  sentryEnabled &&
  sentryAllowSend &&
  isValidSentryDsn(sentryDsn)
) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    // Taxas de amostragem desativadas até validação completa para evitar erros de CORS/Rede
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // ignoreErrors: [
    //   // Translate/extensões mutam o DOM → removeChild (DR-ORNELLAS-25K)
    //   "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
    //   "NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
    // ],
    beforeSend(event) {
      if (isBenignIosNotReadableError(event)) return null;
      return event;
    },
  });

  // eslint-disable-next-line no-console
  console.log('Sentry ativado para o ambiente de produção.');
} else if (process.env.REACT_APP_ENV === 'production') {
  // eslint-disable-next-line no-console
  console.warn(
    'Sentry desativado: Requer REACT_APP_SENTRY_ENABLED=true, REACT_APP_SENTRY_ALLOW_SEND=true e um DSN válido.'
  );
}

const container = document.getElementById('root');

if (!container) {
  throw new Error('Elemento "root" não encontrado no DOM HTML.');
}

// Desregistra eventuais Service Workers legados e limpa URL de cache-busting (_v)
if (typeof window !== 'undefined') {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        for (const registration of registrations) {
          registration.unregister();
        }
      })
      .catch(() => {});
  }

  try {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has('_v')) {
      currentUrl.searchParams.delete('_v');
      window.history.replaceState(
        {},
        document.title,
        currentUrl.pathname + currentUrl.search + currentUrl.hash
      );
    }
  } catch {
    // ignora falha de URL
  }
}

const root = createRoot(container);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
